import { createSlice } from '@reduxjs/toolkit'
import {
  customerAdminResetAlertHandler,
  retrieveCustomerAdminUserByID,
  retrieveCustomerAdminUsers,
  filterCustomerAdminUsers,
  customerAdminEditUser,
  customerAdminCreateUsers,
  customerAdminUserChangeStatus,
  getCountForAllNonRegisterUsers,
  getBulkCreateUsersIsValid,
  getBulkCreateUsersProcessIsValid,
  customerAdminUpdateSelectedUser,
} from './customerAdminThunk'
import { objNotEmpty, updateStateFromQueries } from '../../../../utils/helpers'

const defaultPageSize = 9

const initialState = {
  loading: false,
  pageNumber: 0,
  pageSize: defaultPageSize,
  finalPage: false,
  summaries: {},
  activeSummary: '',
  sortBy: [],
  activeSort: {},
  activeSearch: '',
  value: [],
  updatedUser: {},
  userTypes: [],
  allNonRegisterUsersCount: 0,
  alert: {},
  batchStatusDetails: {},
  filterList: [],
  activeFilters: [],
  bulkCreateUsersIsValid: false,
  bulkCreateUsersProcessIsValid: false,
}

const usersSlice = createSlice({
  name: 'customerAdminUsers',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(customerAdminResetAlertHandler.fulfilled, (state) => {
      state.alert = {}
    })
    builder.addCase(retrieveCustomerAdminUsers.pending, (state, { meta: { arg: { queries } } }) => {
      state.loading = true

      state.pageNumber = queries?.pageNumber ? queries?.pageNumber : 0
      if (!queries?.pageNumber) {
        state.data = []
      }
    })
    builder.addCase(retrieveCustomerAdminUsers.fulfilled, (state, { payload }) => {
      const { response, queries } = payload
      state.loading = false
      state.data = queries?.pageNumber === 0 ? response.users : [...state.data, ...response.users]
      state.summaries = response.metadata
      state.finalPage = response.users.length < state.pageSize
      state.pageNumber = 0
      state.sortBy = response.sort
      state.activeSort =
        (objNotEmpty(state.activeSort) && state.pageNumber >= 1)
          ? state.activeSort : response?.sort?.[0]
      state.activeSummary = queries?.activeSummary ||
        state.activeSummary ||
        Object.keys(response.metadata)[0].toUpperCase()
      state.loading = false
      state.activeSearch = queries?.activeSearch ?? state.activeSearch
      state.filterList = normalizeCustomerAdminFacets(response.facets)
      state.activeFilters = queries?.activeFilters ?? state.activeFilters
      updateStateFromQueries(state, queries)
    })
    builder.addCase(retrieveCustomerAdminUsers.rejected, (state, { payload }) => {
      state.loading = false
      state.alert = payload
    })
    builder.addCase(retrieveCustomerAdminUserByID.pending, (state) => {
      state.loading = true
    })
    builder.addCase(retrieveCustomerAdminUserByID.fulfilled, (state, { payload }) => {
      state.loading = false
      state.updatedUser = payload
    })
    builder.addCase(retrieveCustomerAdminUserByID.rejected, (state, { payload }) => {
      state.loading = false
      state.alert = payload
    })
    builder.addCase(filterCustomerAdminUsers.pending, (state) => {
      state.loading = true
      state.pageNumber = 0
      state.data = []
    })
    builder.addCase(filterCustomerAdminUsers.fulfilled, (state, { payload }) => {
      const { response, queries } = payload
      state.loading = false
      state.data = response.users
      state.summaries = response.metadata
      state.finalPage = response.users.length < state.pageSize
      state.pageNumber = 0
      state.sortBy = response.sort
      state.activeSort = state.activeSort || response.sort[0]
      state.activeSummary = state.activeSummary || Object.keys(response.metadata)[0].toUpperCase()
      state.filterList = normalizeCustomerAdminFacets(response.facets)
      state.activeFilters = queries?.activeFilters || state.activeFilters
    })
    builder.addCase(filterCustomerAdminUsers.rejected, (state, { payload }) => {
      state.loading = false
      state.alert = payload
    })
    builder.addCase(customerAdminEditUser.pending, (state) => {
      state.loading = true
      state.alert = {}
    })
    builder.addCase(customerAdminEditUser.fulfilled, (state, { payload }) => {
      state.loading = false
      state.updatedUser = {}
      state.alert = payload
    })
    builder.addCase(customerAdminEditUser.rejected, (state, { payload }) => {
      state.loading = false
      state.alert = payload
    })
    builder.addCase(customerAdminCreateUsers.pending, (state) => {
      state.loading = true
      state.alert = {}
    })
    builder.addCase(customerAdminCreateUsers.rejected, (state, { payload }) => {
      state.loading = false
      state.alert = payload
    })
    builder.addCase(customerAdminCreateUsers.fulfilled, (state, { payload }) => {
      state.loading = false
      state.updatedUser = {}
      state.alert = payload
    })
    builder.addCase(customerAdminUserChangeStatus.pending, (state) => {
      state.loading = true
      state.finalPage = true
    })
    builder.addCase(customerAdminUserChangeStatus.fulfilled, (state) => {
      state.pageNumber = 0
      state.loading = false
    })
    builder.addCase(customerAdminUserChangeStatus.rejected, (state, { payload }) => {
      state.loading = false
      state.alert = payload
    })
    builder.addCase(getCountForAllNonRegisterUsers.rejected, (state, { payload }) => {
      state.alert = payload
    })
    builder.addCase(getCountForAllNonRegisterUsers.fulfilled, (state, { payload }) => {
      state.allNonRegisterUsersCount = payload?.count || 0
      state.alert = {}
    })
    builder.addCase(getBulkCreateUsersIsValid.rejected, (state, { payload }) => {
      state.alert = payload
    })
    builder.addCase(getBulkCreateUsersIsValid.fulfilled, (state, { payload }) => {
      state.bulkCreateUsersIsValid = payload
      state.alert = {}
    })
    builder.addCase(getBulkCreateUsersProcessIsValid.rejected, (state, { payload }) => {
      state.alert = payload
    })
    builder.addCase(getBulkCreateUsersProcessIsValid.fulfilled, (state, { payload }) => {
      state.bulkCreateUsersProcessIsValid = payload.enabled || false
      state.alert = {}
    })
    builder.addCase(customerAdminUpdateSelectedUser.pending, (state) => {
      state.loading = true
    })
    builder.addCase(customerAdminUpdateSelectedUser.rejected, (state, { payload }) => {
      state.loading = false
      state.alert = payload
    })
    builder.addCase(customerAdminUpdateSelectedUser.fulfilled, (state, { payload }) => {
      state.loading = false
      state.updatedUser = payload
      state.alert = {}
    })
  },
})

export const customerAdminSelectUsers = ({ customerAdminUsers }) => customerAdminUsers

export default usersSlice.reducer

function normalizeCustomerAdminFacets(facets = {}) {
  return normalizeFacetItems(facets?.user_types || [])
}

function normalizeFacetItems(items = []) {
  return (items || []).map((item) => ({
    id: item?.id ?? item?.key,
    name: item?.name ?? item?.value ?? '',
  }))
}
